import sqftShapeManager from "../../../store/sqftShapeManager";
import workspaceControllers from "../../../store/workspaceControllers";
import sqftShapeIntersectionsManager from "../../../store/sqftShapeIntersectionsManager";
import {SHAPE_CORNER_LAYOUTS} from "../../cabinet/cabinetShapes";

/**
 * Auto shift manager for handling group shapes after resize
 */
export default class ShapeResizeAutoShift {
  constructor(shape) {
    this.shape = shape;
    this.shiftedShapes = new Set();
  }

  /**
   * Main function to handle auto shift after width resize
   * @param {number} oldWidth - Previous width of the shape
   * @param {number} newWidth - New width of the shape
   * @returns {Array} Shapes moved as a side effect of the resize.
   */
  handleWidthResizeAutoShift(oldWidth, newWidth) {
    const widthDifference = newWidth - oldWidth;
    if (Math.abs(widthDifference) < 0.1) {
      return [];
    }

    const groupShapes = this.getGroupShapes();
    const cornerCabinets = this.findCornerCabinetsInGroup(groupShapes);

    if (cornerCabinets.length > 0) {
      // If there are corner cabinets, use special logic
      this.handleResizeWithCornerCabinet(widthDifference, 'horizontal', groupShapes, cornerCabinets);
    } else {
      // Normal resize logic
      const shapesToShift = this.findShapesToShift('horizontal');
      this.shiftShapesInGroup(shapesToShift, widthDifference, 'horizontal');
    }

    this.updateWorkspace();
    return this.getShiftedShapes();
  }

  /**
   * Main function to handle auto shift after depth resize
   * @param {number} oldDepth - Previous depth of the shape
   * @param {number} newDepth - New depth of the shape
   * @returns {Array} Shapes moved as a side effect of the resize.
   */
  handleDepthResizeAutoShift(oldDepth, newDepth) {
    const depthDifference = newDepth - oldDepth;
    if (Math.abs(depthDifference) < 0.1) {
      return [];
    }

    const groupShapes = this.getGroupShapes();
    const cornerCabinets = this.findCornerCabinetsInGroup(groupShapes);

    if (cornerCabinets.length > 0) {
      // If there are corner cabinets, use special logic
      this.handleResizeWithCornerCabinet(depthDifference, 'vertical', groupShapes, cornerCabinets);
    } else {
      // Normal resize logic
      const shapesToShift = this.findShapesToShift('vertical');
      this.shiftShapesInGroup(shapesToShift, depthDifference, 'vertical');
    }

    this.updateWorkspace();
    return this.getShiftedShapes();
  }

  /**
   * Handle resize when corner cabinets are present in the group
   * @param {number} sizeDifference - Size difference from resize
   * @param {string} direction - 'horizontal' or 'vertical'
   * @param {Array} groupShapes - All shapes in the group
   * @param {Array} cornerCabinets - Corner cabinets in the group
   */
  handleResizeWithCornerCabinet(sizeDifference, direction, groupShapes, cornerCabinets) {
    // Find the leftmost corner cabinet as anchor
    const anchorCornerCabinet = this.findLeftmostShape(cornerCabinets);

    // Store the original position of the anchor cabinet
    const anchorOriginalPos = anchorCornerCabinet.getPosition();

    // Create array with all shapes including the current shape
    const allShapes = [...groupShapes, this.shape];

    // First, perform normal shifting for all shapes
    const shapesToShift = this.findShapesToShiftIncludingCurrent(direction, allShapes);
    this.shiftShapesInGroup(shapesToShift, sizeDifference, direction);

    // Check how much the anchor corner cabinet moved
    const anchorNewPos = anchorCornerCabinet.getPosition();
    const anchorShiftX = anchorNewPos.x - anchorOriginalPos.x;
    const anchorShiftY = anchorNewPos.y - anchorOriginalPos.y;

    // Move all shapes by the negative of anchor shift to keep anchor in place
    allShapes.forEach(shape => {
      const currentPos = shape.getPosition();
      const correctedPos = {
        x: currentPos.x - anchorShiftX,
        y: currentPos.y - anchorShiftY
      };
      this.moveShapeToPosition(shape, correctedPos);
    });
  }

  /**
   * Find all corner cabinets in a group of shapes
   * @param {Array} shapes - Array of shapes to check
   * @returns {Array} - Array of corner cabinet shapes
   */
  findCornerCabinetsInGroup(shapes) {
    return shapes.filter(shape => this.isCornerCabinet(shape));
  }

  /**
   * Find shapes that need to be shifted in the group (including current shape)
   * @param {string} direction - 'horizontal' for width changes, 'vertical' for depth changes
   * @param {Array} allShapes - All shapes to consider including current
   * @returns {Object} - Object containing shapes to shift in different directions
   */
  findShapesToShiftIncludingCurrent(direction, allShapes) {
    const currentShapePoints = sqftShapeManager.getMinMaxShapesPointsOnWorkspace([this.shape]);

    const shapesToShift = {
      left: [],
      right: [],
      top: [],
      bottom: []
    };

    if (direction === 'horizontal') {
      // For width changes, find all shapes to the left and right
      const currentLeftX = currentShapePoints[0].x;
      const currentRightX = currentShapePoints[1].x;
      const currentCenterX = (currentLeftX + currentRightX) / 2;

      allShapes.forEach(shape => {
        const shapePoints = sqftShapeManager.getMinMaxShapesPointsOnWorkspace([shape]);
        const shapeLeftX = shapePoints[0].x;
        const shapeRightX = shapePoints[1].x;
        const shapeCenterX = (shapeLeftX + shapeRightX) / 2;

        // Shape is to the left of current shape
        if (shapeCenterX < currentCenterX) {
          shapesToShift.left.push(shape);
        }
        // Shape is to the right of current shape
        else if (shapeCenterX > currentCenterX) {
          shapesToShift.right.push(shape);
        }
      });

    } else if (direction === 'vertical') {
      // For depth changes, find all shapes above and below
      const currentTopY = currentShapePoints[0].y;
      const currentBottomY = currentShapePoints[3].y;
      const currentCenterY = (currentTopY + currentBottomY) / 2;

      allShapes.forEach(shape => {
        const shapePoints = sqftShapeManager.getMinMaxShapesPointsOnWorkspace([shape]);
        const shapeTopY = shapePoints[0].y;
        const shapeBottomY = shapePoints[3].y;
        const shapeCenterY = (shapeTopY + shapeBottomY) / 2;

        // Shape is above current shape
        if (shapeCenterY < currentCenterY) {
          shapesToShift.top.push(shape);
        }
        // Shape is below current shape
        else if (shapeCenterY > currentCenterY) {
          shapesToShift.bottom.push(shape);
        }
      });
    }

    return shapesToShift;
  }

  /**
   * Find shapes that need to be shifted in the group
   * @param {string} direction - 'horizontal' for width changes, 'vertical' for depth changes
   * @returns {Object} - Object containing shapes to shift in different directions
   */
  findShapesToShift(direction) {
    const currentShapePoints = sqftShapeManager.getMinMaxShapesPointsOnWorkspace([this.shape]);
    const groupShapes = this.getGroupShapes();

    const shapesToShift = {
      left: [],
      right: [],
      top: [],
      bottom: []
    };

    if (direction === 'horizontal') {
      // For width changes, find all shapes to the left and right
      const currentLeftX = currentShapePoints[0].x;
      const currentRightX = currentShapePoints[1].x;
      const currentCenterX = (currentLeftX + currentRightX) / 2;

      groupShapes.forEach(shape => {
        const shapePoints = sqftShapeManager.getMinMaxShapesPointsOnWorkspace([shape]);
        const shapeLeftX = shapePoints[0].x;
        const shapeRightX = shapePoints[1].x;
        const shapeCenterX = (shapeLeftX + shapeRightX) / 2;

        // Shape is to the left of current shape
        if (shapeCenterX < currentCenterX) {
          shapesToShift.left.push(shape);
        }
        // Shape is to the right of current shape
        else if (shapeCenterX > currentCenterX) {
          shapesToShift.right.push(shape);
        }
      });

    } else if (direction === 'vertical') {
      // For depth changes, find all shapes above and below
      const currentTopY = currentShapePoints[0].y;
      const currentBottomY = currentShapePoints[3].y;
      const currentCenterY = (currentTopY + currentBottomY) / 2;

      groupShapes.forEach(shape => {
        const shapePoints = sqftShapeManager.getMinMaxShapesPointsOnWorkspace([shape]);
        const shapeTopY = shapePoints[0].y;
        const shapeBottomY = shapePoints[3].y;
        const shapeCenterY = (shapeTopY + shapeBottomY) / 2;

        // Shape is above current shape
        if (shapeCenterY < currentCenterY) {
          shapesToShift.top.push(shape);
        }
        // Shape is below current shape
        else if (shapeCenterY > currentCenterY) {
          shapesToShift.bottom.push(shape);
        }
      });
    }

    return shapesToShift;
  }

  /**
   * Shift all shapes in group based on resize
   * @param {Object} shapesToShift - Object containing shapes to shift
   * @param {number} sizeDifference - How much the shape size changed
   * @param {string} direction - 'horizontal' or 'vertical'
   */
  shiftShapesInGroup(shapesToShift, sizeDifference, direction) {
    const halfShift = sizeDifference / 2;

    if (direction === 'horizontal') {
      // Shift all shapes to the left (move them further left)
      shapesToShift.left.forEach((shape) => {
        const currentPos = shape.getPosition();
        const newPos = {
          x: currentPos.x - halfShift,
          y: currentPos.y
        };
        this.moveShapeToPosition(shape, newPos);
      });

      // Shift all shapes to the right (move them further right)
      shapesToShift.right.forEach((shape) => {
        const currentPos = shape.getPosition();
        const newPos = {
          x: currentPos.x + halfShift,
          y: currentPos.y
        };
        this.moveShapeToPosition(shape, newPos);
      });

    } else if (direction === 'vertical') {
      // Shift all shapes above (move them further up)
      shapesToShift.top.forEach((shape) => {
        const currentPos = shape.getPosition();
        const newPos = {
          x: currentPos.x,
          y: currentPos.y - halfShift
        };
        this.moveShapeToPosition(shape, newPos);
      });

      // Shift all shapes below (move them further down)
      shapesToShift.bottom.forEach((shape) => {
        const currentPos = shape.getPosition();
        const newPos = {
          x: currentPos.x,
          y: currentPos.y + halfShift
        };
        this.moveShapeToPosition(shape, newPos);
      });
    }
  }

  /**
   * Get shapes from the same group that should be shifted
   * Uses the same logic as auto shuffle system
   */
  getGroupShapes() {
    return workspaceControllers.snappedShapeGroups.getSnappedShapesForTheShape(this.shape);
  }

  /**
   * Move shape to new position and update absolute position
   */
  moveShapeToPosition(shape, newPosition) {
    const currentPosition = shape.getPosition();
    const didMove = Math.abs(currentPosition.x - newPosition.x) > 0.001
      || Math.abs(currentPosition.y - newPosition.y) > 0.001;

    shape.setPosition(newPosition);
    shape.shapeAbsolute.setAbsolutePos(newPosition);

    if (didMove) {
      this.shiftedShapes.add(shape);
    }
  }

  getShiftedShapes() {
    return Array.from(this.shiftedShapes);
  }

  /**
   * Find the leftmost shape among given shapes
   * @param {Array} shapes - Array of shapes to compare
   * @returns {Object} - The leftmost shape
   */
  findLeftmostShape(shapes) {
    let leftmostShape = shapes[0];
    let leftmostX = sqftShapeManager.getMinMaxShapesPointsOnWorkspace([leftmostShape])[0].x;

    shapes.forEach(shape => {
      const shapePoints = sqftShapeManager.getMinMaxShapesPointsOnWorkspace([shape]);
      const shapeLeftX = shapePoints[0].x;

      if (shapeLeftX < leftmostX) {
        leftmostShape = shape;
        leftmostX = shapeLeftX;
      }
    });

    return leftmostShape;
  }

  /**
   * Check if shape is a corner cabinet
   * @param {Object} shape - Shape to check
   * @returns {boolean} - True if it's a corner cabinet
   */
  isCornerCabinet(shape) {
    return typeof SHAPE_CORNER_LAYOUTS !== 'undefined' &&
      SHAPE_CORNER_LAYOUTS.includes(shape.shapeParams.shapeLayout);
  }

  /**
   * Update workspace after shifting shapes
   */
  updateWorkspace() {
    // Update intersections and snapped groups (similar to auto shuffle)
    if (typeof sqftShapeIntersectionsManager !== 'undefined') {
      sqftShapeIntersectionsManager.updateShapesIntersections();
    }
    if (typeof workspaceControllers !== 'undefined') {
      workspaceControllers.snappedShapeGroups.updateShapesSnappedGroups(false);
    }
  }
}
